import React, { useEffect, lazy, Suspense, useContext, useState } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthContext, AuthProvider } from "./context/AuthContext";
import Navbar from "./components/Navbar";
import GlobalSearchOverlay from "./components/GlobalSearchOverlay";
import LoadingScreen from "./components/common/LoadingScreen";

const Home = lazy(() => import("./pages/Home"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const Landing = lazy(() => import("./pages/Landing"));
const CustomerForm = lazy(() => import("./pages/CustomerForm"));
const ITRForm = lazy(() => import("./pages/ITRForm"));
const ITRHistory = lazy(() => import("./pages/ITRHistory"));
const Subadmin = lazy(() => import("./pages/Subadmin"));
const Ca = lazy(() => import("./pages/Ca"));
const Superadmin = lazy(() => import("./pages/Superadmin"));
const SubadminDashboard = lazy(() => import("./pages/SubadminDashboard"));
const SubadminCustomers = lazy(() => import("./pages/SubadminCustomers"));
const SubadminAgents = lazy(() => import("./pages/SubadminAgents"));
// const SubadminSettings = lazy(()=>import("./pages/SubadminSettings"));
const SubadminReports = lazy(() => import("./pages/SubadminReports"));
const SubadminChatSystem = lazy(() => import("./pages/SubadminChatSystem"));
const SubadminEverificationOtpcheck = lazy(() => import("./pages/SubadminEverificationOtpcheck"));
const CaReports = lazy(() => import("./pages/CaReports"));


function App() {
  const location = useLocation();
  const { user, loading, login, logout, isCollapsed, permissions } = useContext(AuthContext);
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);

  useEffect(() => {
    if (user && user.isagent === 'unverified') {
      setShowVerificationPopup(true);
    } else {
      setShowVerificationPopup(false);
    }
  }, [user]);

  useEffect(() => {
    // Cleanup function
    return () => {
      // Add any cleanup logic here if needed
    };
  }, [location.pathname]);

  if (loading) {
    return <LoadingScreen />;
  }

  const ProtectedRoute = ({ children, permission }) => {
    // Check permissions from context (reactive) or fallback to storage
    const activePermissions = permissions || JSON.parse(localStorage.getItem('agentPermissions')) || JSON.parse(localStorage.getItem('subadminPermissions')) || [];

    if (permission && !activePermissions.includes(permission)) {
      if (user?.issubadmin) {
        // Redirection logic for subadmins without specific permission
        const subadminRoutes = [
          { perm: 'manage_itr', path: '/subadmin/customers' },
          { perm: 'manage_agents', path: '/subadmin/agents' },
          { perm: 'view_reports', path: '/subadmin/reports' },
          { perm: 'manage_chat_room', path: '/subadmin/settings' },
          { perm: 'manage_everification_otpcheck', path: '/subadmin/everification-otpcheck' }
        ];
        const firstAvailable = subadminRoutes.find(r => activePermissions.includes(r.perm));
        return <Navigate to={firstAvailable ? firstAvailable.path : '/login'} replace />;
      }
      return <Navigate to="/itr-application" replace />;
    }
    return children;
  };

  return (
    <Suspense fallback={<LoadingScreen />}>
      <div className={isCollapsed ? "sidebar-collapsed" : ""}>
        {location.pathname !== '/login' && location.pathname !== '/signup' && location.pathname !== '/landing' && <Navbar />}
        <GlobalSearchOverlay />
        {showVerificationPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm text-center">
              <h2 className="text-xl font-bold mb-4">Verification Pending</h2>
              <p>Your agent account is currently unverified. Please wait for verification.</p>
              <button
                className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                onClick={() => setShowVerificationPopup(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}
        <Routes>
          {!user ? (
            <>
              <Route path="/login" element={<Login onLogin={login} />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/landing" element={<Landing />} />
              <Route path="*" element={<Navigate to="/landing" replace />} />
            </>
          ) : user.issuperadmin ? (
            <>
              <Route path="/superadmin" element={<Superadmin />} />
              <Route path="/superadmin/agents" element={<Superadmin />} />
              <Route path="/superadmin/subadmins" element={<Superadmin />} />
              <Route path="/superadmin/cas" element={<Superadmin />} />
              <Route path="/superadmin/customers" element={<Superadmin />} />
              <Route path="/superadmin/payments" element={<Superadmin />} />
              <Route path="/superadmin/reports" element={<Superadmin />} />
              <Route path="/superadmin/customer-form-fields" element={<Superadmin />} />
              <Route path="/superadmin/ITR" element={<Superadmin />} />
              <Route path="/superadmin/wallets" element={<Superadmin />} />
              <Route path="/superadmin/chat-rooms" element={<Superadmin />} />
              <Route path="/superadmin/ratecard" element={<Superadmin />} />
              <Route path="*" element={<Navigate to="/superadmin" replace />} />
            </>
          ) : user.issubadmin ? (
            <>
              {/* <Route path="/subadmin" element={<SubadminDashboard />} /> */}
              <Route path="/subadmin/customers" element={
                <ProtectedRoute permission="manage_itr">
                  <SubadminCustomers />
                </ProtectedRoute>
              } />
              <Route path="/subadmin/agents" element={
                <ProtectedRoute permission="manage_agents">
                  <SubadminAgents />
                </ProtectedRoute>
              } />
              <Route path="/subadmin/reports" element={
                <ProtectedRoute permission="view_reports">
                  <SubadminReports />
                </ProtectedRoute>
              } />
              <Route path="/subadmin/settings" element={
                <ProtectedRoute permission="manage_chat_room">
                  <SubadminChatSystem />
                </ProtectedRoute>
              } />
              <Route path="/subadmin/everification-otpcheck" element={
                <ProtectedRoute permission="manage_everification_otpcheck">
                  <SubadminEverificationOtpcheck />
                </ProtectedRoute>
              } />
              <Route path="*" element={
                (() => {
                  const activePermissions = permissions || JSON.parse(localStorage.getItem('subadminPermissions')) || [];
                  const subadminRoutes = [
                    { perm: 'manage_itr', path: '/subadmin/customers' },
                    { perm: 'manage_agents', path: '/subadmin/agents' },
                    { perm: 'view_reports', path: '/subadmin/reports' },
                    { perm: 'manage_chat_room', path: '/subadmin/settings' },
                    { perm: 'manage_everification_otpcheck', path: '/subadmin/everification-otpcheck' }
                  ];
                  const firstAvailable = subadminRoutes.find(r => activePermissions.includes(r.perm));
                  return <Navigate to={firstAvailable ? firstAvailable.path : '/login'} replace />;
                })()
              } />
            </>
          ) : user.isca ? (
            <>
              <Route path="/ca" element={<Ca />} />
              <Route path="/ca/reports" element={<CaReports />} />
              <Route path="*" element={<Navigate to="/ca" replace />} />
            </>
          ) : (
            <>
              <Route path="/itr-application" element={
                <ProtectedRoute permission="manage_customer">
                  <Home />
                </ProtectedRoute>
              } />
              <Route path="/customer" element={
                <ProtectedRoute permission="manage_customer">
                  <Home />
                </ProtectedRoute>
              } />
              <Route path="/wallet" element={
                <ProtectedRoute permission="manage_wallet">
                  <Home />
                </ProtectedRoute>
              } />
              <Route path="/customer-form" element={
                <ProtectedRoute permission="manage_add_new_itr">
                  <CustomerForm />
                </ProtectedRoute>
              } />
              <Route path="/itr" element={
                <ProtectedRoute permission="manage_add_new_itr">
                  <ITRForm />
                </ProtectedRoute>
              } />
              <Route path="/itr-history" element={
                <ProtectedRoute permission="manage_itr_history">
                  <ITRHistory />
                </ProtectedRoute>
              } />
              {/* <Route path="*" element={<Navigate to="/itr-application" replace />} /> */}
            </>
          )}
        </Routes>
      </div>
    </Suspense>
  );
}

export default function AppWrapper() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}
